/*
 * Text anchors for comments on a rendered Word document.
 *
 * Ported from the HTML review tool (`html-review-tool/server.js`, the injected overlay —
 * `findTextNode`, `applyHighlight`, `removeHighlights`, and the selection handler). The model
 * is the same: a comment stores the SELECTED TEXT plus up to 40 characters before and after
 * it, and is re-found by text — so it survives re-rendering, and a phrase that appears twice
 * is told apart by what precedes it.
 *
 * Differences from the original: it runs on a React-owned container instead of an iframe's
 * body (no postMessage), and a selection that spans elements is matched across text nodes
 * (Word paragraphs routinely split a sentence into several runs).
 */

const CONTEXT = 40;
const MARK = 'drv-hl';

function textNodes(root) {
  const out = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const p = n.parentElement;
      if (!p || p.tagName === 'SCRIPT' || p.tagName === 'STYLE') return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  // eslint-disable-next-line no-cond-assign
  while ((n = walker.nextNode())) out.push(n);
  return out;
}

/** The selection inside `root` as an anchor: {selected_text, before, after}, or null. */
export function selectionAnchor(root) {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;
  const text = sel.toString().replace(/\s+/g, ' ').trim();
  if (!text || text.length < 2) return null;

  // Context from the whole document's text, located by the range's start — robust even when
  // the selection crosses runs or paragraphs.
  const nodes = textNodes(root);
  let offset = 0;
  let start = -1;
  for (const n of nodes) {
    if (n === range.startContainer) { start = offset + range.startOffset; break; }
    offset += n.textContent.length;
  }
  const full = nodes.map((n) => n.textContent).join('');
  const norm = (s) => s.replace(/\s+/g, ' ');
  const before = start >= 0 ? norm(full.slice(Math.max(0, start - CONTEXT), start)) : '';
  const afterStart = start >= 0 ? start + sel.toString().length : -1;
  const after = afterStart >= 0 ? norm(full.slice(afterStart, afterStart + CONTEXT)) : '';
  const rect = range.getBoundingClientRect();
  return { selected_text: text, before, after, rect };
}

/** Where `anchor` sits in the concatenated text of `nodes`: [start, end] or null. */
function locate(full, anchor) {
  const text = anchor.selected_text || '';
  if (!text) return null;
  // Whitespace-tolerant search: the stored text was whitespace-collapsed.
  const esc = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
  const re = new RegExp(esc, 'g');
  const hits = [];
  let m;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(full))) { hits.push([m.index, m.index + m[0].length]); if (!m[0].length) break; }
  if (!hits.length) return null;
  if (hits.length === 1 || !anchor.before) return hits[0];
  // Several occurrences: the one whose preceding text best matches `before`.
  const tail = anchor.before.replace(/\s+/g, ' ').slice(-20);
  const scored = hits.map(([s, e]) => {
    const prev = full.slice(Math.max(0, s - 60), s).replace(/\s+/g, ' ');
    return { s, e, ok: prev.endsWith(tail) ? 2 : prev.includes(tail.trim()) ? 1 : 0 };
  });
  scored.sort((a, b) => b.ok - a.ok);
  return [scored[0].s, scored[0].e];
}

/** Remove every highlight we added, restoring the original text nodes. */
export function clearHighlights(root) {
  root.querySelectorAll(`span.${MARK}`).forEach((el) => {
    const p = el.parentNode;
    while (el.firstChild) p.insertBefore(el.firstChild, el);
    p.removeChild(el);
  });
  root.normalize();
}

/**
 * Wrap each text-anchored comment's span in `<span class="drv-hl" data-cid>`.
 * A span crossing several text nodes is wrapped piece by piece. Returns the ids that
 * could NOT be found (text since changed) so the sidebar can say so.
 */
export function applyHighlights(root, comments, onClick) {
  clearHighlights(root);
  const missing = [];
  comments.forEach((c) => {
    const nodes = textNodes(root);
    const full = nodes.map((n) => n.textContent).join('');
    const at = locate(full, c.anchor || {});
    if (!at) { missing.push(c.id); return; }
    const [s, e] = at;
    let pos = 0;
    const pieces = [];
    nodes.forEach((n) => {
      const len = n.textContent.length;
      const a = Math.max(s, pos);
      const b = Math.min(e, pos + len);
      if (a < b) pieces.push([n, a - pos, b - pos]);
      pos += len;
    });
    pieces.forEach(([n, a, b]) => {
      try {
        const range = document.createRange();
        range.setStart(n, a);
        range.setEnd(n, b);
        const span = document.createElement('span');
        span.className = `${MARK} ${MARK}--${c.kind}${c.is_ai ? ` ${MARK}--ai` : ''}${c.status !== 'open' ? ` ${MARK}--closed` : ''}`;
        span.dataset.cid = c.id;
        span.addEventListener('click', (ev) => { ev.stopPropagation(); onClick(c.id); });
        range.surroundContents(span);
      } catch (_) { /* a piece that can't be wrapped is left plain */ }
    });
  });
  return missing;
}

/** Scroll a comment's highlight into view and mark it as the focused one. */
export function focusHighlight(root, cid) {
  root.querySelectorAll(`span.${MARK}--focus`).forEach((el) => el.classList.remove(`${MARK}--focus`));
  const els = root.querySelectorAll(`span.${MARK}[data-cid="${cid}"]`);
  els.forEach((el) => el.classList.add(`${MARK}--focus`));
  if (els[0] && els[0].scrollIntoView) els[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
  return els.length > 0;
}
